import StyledTooltip from './styles';

/**
 * PropTypes é um mecanismo utilizado para garantir que os 
 * componentes usem o tipo de dados correto e passem os dados corretos
 */
import PropTypes from 'prop-types';
/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional
 */
import {
  isValidElement,
  Children,
  cloneElement,
  useState,
  useEffect,
} from 'react';

export const Tooltip = ({
  title,
  description,
  children,
  disabled = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [wasLeave, setWasLeave] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);

  /**
   * Devido a um erro recursivo de comportamento do Tooltip
   * Material UI foi necessário criar esse trecho de código
   * para bloquear a escuta deles aos eventos globais de
   * scroll, vide:
   * http://dev.inema.ba.gov.br/AMBIENTAL/GEOBAHIA/projeto/Geobahia/novo-geobahia-publico/issues/1031
   */
  useEffect(() => {
    let timeoutId = null;
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setIsScrolling(false), 500);
    };

    window.addEventListener('DOMMouseScroll', handleScroll, false);
    window.addEventListener('mousewheel', handleScroll, false);

    return () => {
      window.removeEventListener('DOMMouseScroll', handleScroll, false);
      window.removeEventListener('mousewheel', handleScroll, false);
    };
  }, []);

  // Passando novos eventHandlers para o children, para controlar a abertura e fechamento do Tooltip
  const newChildren = Children.map(children, (child, i) => {
    if (isValidElement(child)) {
      if (i === 0)
        return cloneElement(child, {
          ...child.props,
          onClick: (e) => {
            child.props.onClick && child.props.onClick(e);
            setIsOpen(false);
          },
          onMouseLeave: (e) => {
            child.props.onMouseLeave && child.props.onMouseLeave(e);
            setIsOpen(false);
            setWasLeave(true);
          },
          onMouseOver: (e) => {
            child.props.onMouseOver && child.props.onMouseOver(e);
            if (!props.disableHoverListener) {
              setWasLeave(false);
              wasLeave && setIsOpen(true);
            }
          },
          onTouchEnd: (e) => {
            child.props.onTouchEnd && child.props.onTouchEnd(e);
            setIsOpen(false);
            setWasLeave(true);
          },
          onTouchStart: (e) => {
            child.props.onTouchStart && child.props.onTouchStart(e);
            if (!props.disableTouchListener) {
              setWasLeave(false);
              wasLeave && setIsOpen(true);
            }
          },
        });
      return child;
    }
    return child;
  });

  return (
    <StyledTooltip
      title={
        description && title ? (
          <div>
            <strong>{title}</strong>
            <br />
            {description}
          </div>
        ) : (
          title
        )
      }
      open={isOpen && !disabled && !isScrolling}
      {...props}
    >
      {newChildren[0]}
    </StyledTooltip>
  );
};

Tooltip.propTypes = {
  /** Titulo */
  title: PropTypes.string.isRequired,
  /** Descrição */
  description: PropTypes.string,
  /** Elemento que deseja inserir o Tolltip */
  children: PropTypes.any,
  disabled: PropTypes.bool,
};

export default Tooltip;
